import {Accordion, Button} from '@mantine/core';
import {Container} from 'src/design/atoms/Container';
import {List} from 'src/design/atoms/List';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {Link} from 'react-router-dom';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';

interface IFaq {
  label: string;
  content: () => React.ReactNode;
}

const faqs: IFaq[] = [
  {
    label: 'Why is my account unconfirmed?',
    content: () => (
      <Text c="dimmed" size="xs">
        Your account must be confirmed before you can log in. Please check your
        email for a confirmation link. Click the link to confirm your account
        and create your password. If you don't see it, contact your
        administrator for help.
      </Text>
    ),
  },
  {
    label: 'Not getting my login or password reset email',
    content: () => (
      <Stack>
        <List c="dimmed" size="xs" spacing="xs">
          <List.Item>
            Make sure you already have an account - new users won't receive
            these emails. If you're not sure, contact your administrator.
          </List.Item>
          <List.Item>
            Check your spam/junk folder and mark our email as "not spam" if you
            find it there
          </List.Item>
          <List.Item>
            Add no-reply@proactehs.com to your email contacts
          </List.Item>
          <List.Item>
            If you're at work, ask IT to allow emails from
            no-reply@proactehs.com
          </List.Item>
        </List>
        <Text c="dimmed" size="xs">
          Still having trouble? Contact your administrator for help.
        </Text>
      </Stack>
    ),
  },
  {
    label: 'How do I get an account?',
    content: () => (
      <Text c="dimmed" size="xs">
        Contact your administrator to set up your account. Only contractors with
        special permission can create their own accounts.
      </Text>
    ),
  },
  {
    label: 'Having trouble with my password',
    content: () => (
      <Stack>
        <Text c="dimmed" size="xs">
          If your password isn't working, here are some things to check:
        </Text>
        <List c="dimmed" size="xs" spacing="xs">
          <List.Item>
            Double-check you're using the right email address
          </List.Item>
          <List.Item>
            Make sure Caps Lock is off when typing your password
          </List.Item>
          <List.Item>
            Click "Forgot password?" if you can't remember it
          </List.Item>
          <List.Item>
            Still stuck? Contact your administrator for help
          </List.Item>
        </List>
      </Stack>
    ),
  },
  {
    label: 'Need to reset my password',
    content: () => (
      <Text c="dimmed" size="xs">
        Click "Forgot password?" on the login page. We'll email you a link to
        create a new password. You can only use the link once.
      </Text>
    ),
  },
  {
    label: 'What is a passwordless login?',
    content: () => (
      <Text c="dimmed" size="xs">
        Instead of typing a password, we can send a special login link to your
        email. Just click the link to sign in. Each link works for 60 minutes
        and can only be used once.
      </Text>
    ),
  },
  {
    label: 'Why do I need to set a password when using an email link?',
    content: () => (
      <Stack>
        <Text c="dimmed" size="xs">
          If you're a new user or recently reset your password, you'll need to
          set up your password first.
        </Text>
        <Text c="dimmed" size="xs">
          This helps keep your account secure.
        </Text>
        <Text c="dimmed" size="xs">
          After setting your password, use the same email link again to log in.
        </Text>
      </Stack>
    ),
  },
  {
    label: 'Email is already in use',
    content: () => (
      <Stack>
        <Text c="dimmed" size="xs">
          This means you already have an account with this email address.
        </Text>
        <List c="dimmed" size="xs" spacing="xs">
          <List.Item>Make sure you typed your email correctly</List.Item>
          <List.Item>
            Try the "Forgot password?" link if you can't log in
          </List.Item>
          <List.Item>Need help? Contact your administrator</List.Item>
        </List>
      </Stack>
    ),
  },
  {
    label: 'Why is my account inactive?',
    content: () => (
      <Text c="dimmed" size="xs">
        Your administrator needs to activate your account before you can use it,
        even if you've logged in before. If your account is inactive, please
        contact them to get it activated.
      </Text>
    ),
  },
];
const SupportPage = () => {
  return (
    <Container size={450} my={40}>
      <Stack>
        <Title ta="center" order={2}>
          Login Support
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          Frequently asked questions
        </Text>
        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack>
            <Accordion>
              {faqs.map((faq, index) => (
                <Accordion.Item key={index} value={index.toString()}>
                  <Accordion.Control>
                    <Text size="sm">{faq.label}</Text>
                  </Accordion.Control>
                  <Accordion.Panel>{faq.content()}</Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>

            <Button
              mt="xl"
              component={Link}
              to={PathUtility.Login()}
              aria-label="back to login button">
              Back to login
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

// Register the route
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Support(),
  element: <SupportPage />,
  isPrivate: false,
});

export {SupportPage};
